import {
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  Undo2,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  useCarriers,
  useMarkDelivered,
  useParcel,
  useProjects,
  useRefreshParcel,
  useDeleteParcel,
  useSetParcelHidden,
  useUpdateParcel,
} from '../api/client'
import { useIsAdmin } from '../api/auth'
import { de } from '../i18n/de'
import { formatDateTime, formatDeliveryWindow, formatMoment } from '../lib/format'
import { isOrderKeyed } from '../lib/parcel'
import { STATUS_META, lastRouteStatus } from '../lib/status'
import type { ParcelLink } from '../api/types'
import {
  AddParcelDialog,
  dangerButton,
  inputClass,
  primaryButton,
  secondaryButton,
} from './AddParcelDialog'
import { CarrierBadge } from './CarrierBadge'
import { FavoriteStar } from './FavoriteStar'
import { StatusRoute } from './StatusRoute'
import { TrackingNumber } from './TrackingNumber'

export function ParcelDrawer({
  parcelId,
  onClose,
  onOpenParcel,
}: {
  parcelId: number
  onClose: () => void
  /** Switch the drawer to another parcel: a linked return or original, a new return. */
  onOpenParcel?: (id: number) => void
}) {
  const { data: parcel, isLoading } = useParcel(parcelId)
  const { data: projects } = useProjects()
  const { data: carriers } = useCarriers()
  const updateParcel = useUpdateParcel()
  const refreshParcel = useRefreshParcel()
  const markDelivered = useMarkDelivered()
  const setHidden = useSetParcelHidden()
  const deleteParcel = useDeleteParcel()
  const isAdmin = useIsAdmin()
  const [sendingBack, setSendingBack] = useState(false)

  const [form, setForm] = useState({
    description: '',
    sender_name: '',
    recipient_name: '',
    recipient_address: '',
    order_number: '',
    notes: '',
  })
  const [refreshNote, setRefreshNote] = useState<string | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  useEffect(() => {
    if (!parcel) return
    setForm({
      description: parcel.description ?? '',
      sender_name: parcel.sender_name ?? '',
      recipient_name: parcel.recipient_name ?? '',
      recipient_address: parcel.recipient_address ?? '',
      order_number: parcel.order_number ?? '',
      notes: parcel.notes ?? '',
    })
  }, [parcel])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const orderKeyed = parcel ? isOrderKeyed(parcel) : false

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <aside
        onClick={(event) => event.stopPropagation()}
        className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl dark:bg-slate-900"
      >
        {isLoading || !parcel ? (
          <div className="p-6 text-sm text-slate-500">…</div>
        ) : (
          <>
            <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <FavoriteStar parcelId={parcel.id} favorite={parcel.favorite} size="md" />
                    {parcel.is_return && <ReturnBadge />}
                    {parcel.hidden_at && (
                      <span
                        title={
                          parcel.hidden_by
                            ? de.parcel.hiddenBy(parcel.hidden_by)
                            : de.parcel.hiddenBadge
                        }
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                      >
                        <EyeOff className="h-3 w-3" aria-hidden />
                        {de.parcel.hiddenBadge}
                      </span>
                    )}
                    <CarrierBadge
                      code={parcel.carrier_code}
                      name={parcel.carrier_name}
                      confidence={parcel.carrier_confidence}
                    />
                  </div>
                  <h2 className="truncate text-lg font-semibold">
                    {parcel.description ?? de.parcel.noDescription}
                  </h2>
                  <TrackingNumber
                    value={orderKeyed ? parcel.order_number! : parcel.tracking_number}
                    display={orderKeyed ? parcel.order_number! : undefined}
                    label={orderKeyed ? de.parcel.orderNumber : de.parcel.trackingNumber}
                  />
                </div>
                <button
                  onClick={onClose}
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label={de.actions.close}
                >
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>

              <div className="mt-4">
                <StatusRoute
                  status={parcel.status}
                  reached={lastRouteStatus(parcel.events)}
                  size="lg"
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {parcel.tracking_url && (
                  <a
                    href={parcel.tracking_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={secondaryButton + ' inline-flex items-center gap-1.5'}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    {orderKeyed ? de.parcel.openAtAmazon : de.parcel.openAtCarrier}
                  </a>
                )}
                <button
                  onClick={() =>
                    refreshParcel.mutate(parcel.id, {
                      onSuccess: (result) => {
                        setRefreshNote(result.message)
                        setRefreshError(null)
                      },
                      onError: (error) => setRefreshError((error as Error).message),
                    })
                  }
                  disabled={refreshParcel.isPending}
                  className={secondaryButton + ' inline-flex items-center gap-1.5'}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${refreshParcel.isPending ? 'animate-spin' : ''}`}
                    aria-hidden
                  />
                  {de.actions.refreshNow}
                </button>
              </div>
              {refreshError ? (
                <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{refreshError}</p>
              ) : (
                refreshNote && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{refreshNote}</p>
                )
              )}
            </header>

            <div className="space-y-7 px-5 py-5">
              {(parcel.return_of || parcel.returns.length > 0) && (
                <div className="space-y-1 text-sm">
                  {parcel.return_of && (
                    <LinkedParcel
                      label={de.parcel.returnOf}
                      parcel={parcel.return_of}
                      onOpen={onOpenParcel}
                    />
                  )}
                  {parcel.returns.map((other) => (
                    <LinkedParcel
                      key={other.id}
                      label={de.parcel.returns}
                      parcel={other}
                      onOpen={onOpenParcel}
                    />
                  ))}
                </div>
              )}
              {/* The facts people open a parcel for: when it comes, where to, how fresh. */}
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800/50">
                <Detail label={de.parcel.expected}>
                  {formatDeliveryWindow(
                    parcel.expected_delivery_from,
                    parcel.expected_delivery_to,
                  )}
                </Detail>
                <Detail label={de.parcel.refreshed}>{formatMoment(parcel.refreshed_at)}</Detail>
                {parcel.delivered_at && (
                  <Detail label={de.parcel.delivered}>
                    {formatDateTime(parcel.delivered_at)}
                  </Detail>
                )}
                {parcel.pickup_location && (
                  <Detail label={de.parcel.pickupLocation} wide>
                    {parcel.pickup_location}
                  </Detail>
                )}
                {/* The address is how people tell where a parcel is going -- one of two
                    company sites, or someone's home. */}
                {parcel.recipient_address && (
                  <Detail label={de.parcel.recipientAddress} wide>
                    <span className="whitespace-pre-line">{parcel.recipient_address}</span>
                    {parcel.recipient_address_partial && (
                      <span className="ml-1 text-xs text-slate-400">({de.parcel.onlyCity})</span>
                    )}
                  </Detail>
                )}
                <Detail label={de.parcel.statusSource} wide>
                  <span
                    title={
                      parcel.status_source === 'api'
                        ? de.parcel.statusSourceApiHint
                        : undefined
                    }
                  >
                    {parcel.status_source === 'api'
                      ? de.parcel.statusSourceApi
                      : parcel.status_source === 'manual'
                        ? de.parcel.statusSourceManual
                        : de.parcel.statusSourceEmail}
                  </span>
                </Detail>
              </dl>

              {/* What happened to it, right after the facts. Everything editable follows. */}
              <section>
                <h3 className={`${sectionHeading} mb-3`}>{de.parcel.timeline}</h3>
                <ol className="space-y-3.5 border-l border-slate-200 pl-4 dark:border-slate-700">
                  {parcel.events.map((event, index) => (
                    <li key={event.id} className="relative">
                      {/* The newest event *is* the parcel's current state, so it carries
                          the emphasis rather than being one row among equals. */}
                      <span
                        className={`absolute top-1.5 rounded-full ring-2 ring-white dark:ring-slate-900 ${
                          STATUS_META[event.status].dot
                        } ${index === 0 ? '-left-[1.38rem] h-2.5 w-2.5' : '-left-[1.32rem] h-2 w-2'}`}
                        aria-hidden
                      />
                      <p
                        className={
                          index === 0
                            ? 'text-sm font-medium text-slate-900 dark:text-slate-100'
                            : 'text-sm text-slate-700 dark:text-slate-300'
                        }
                      >
                        {event.description ?? STATUS_META[event.status].label}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDateTime(event.occurred_at)}
                        {event.location && ` · ${event.location}`}
                        {` · ${event.source}`}
                      </p>
                    </li>
                  ))}
                  {parcel.events.length === 0 && (
                    <li className="text-sm text-slate-400">{de.parcel.noEvents}</li>
                  )}
                </ol>
              </section>

              <section className="space-y-3">
                <h3 className={sectionHeading}>{de.parcel.sectionEdit}</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {/* Correcting a guessed carrier re-runs detection and schedules a lookup. */}
                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                      {de.parcel.carrier}
                    </span>
                    <select
                      value={parcel.carrier_code}
                      onChange={(event) =>
                        updateParcel.mutate({
                          id: parcel.id,
                          patch: { carrier: event.target.value },
                        })
                      }
                      className={inputClass}
                    >
                      {carriers?.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    {parcel.carrier_confidence !== 'certain' && (
                      <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                        {de.confidence[parcel.carrier_confidence]}
                      </span>
                    )}
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                      {de.parcel.project}
                    </span>
                    <select
                      value={parcel.project_id ?? ''}
                      onChange={(event) =>
                        updateParcel.mutate({
                          id: parcel.id,
                          patch: {
                            project_id: event.target.value ? Number(event.target.value) : null,
                          },
                        })
                      }
                      className={inputClass}
                    >
                      <option value="">{de.parcel.noProject}</option>
                      {projects?.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {(
                  [
                    ['description', de.parcel.description],
                    ['sender_name', de.parcel.sender],
                    ['recipient_name', de.parcel.recipient],
                    ['recipient_address', de.parcel.recipientAddress],
                    ['order_number', de.parcel.orderNumber],
                  ] as const
                ).map(([field, label]) => (
                  <label key={field} className="block">
                    <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                      {label}
                    </span>
                    {field === 'order_number' && orderKeyed ? (
                      // The order number *is* this parcel's identity (see isOrderKeyed),
                      // so it isn't editable here — only shown for reference.
                      <>
                        <input
                          value={form.order_number}
                          disabled
                          readOnly
                          className={inputClass + ' cursor-not-allowed opacity-60'}
                        />
                        <span className="mt-1 block text-xs text-slate-400">
                          {de.parcel.orderNumberFixed}
                        </span>
                      </>
                    ) : field === 'recipient_address' ? (
                      <>
                        <textarea
                          rows={2}
                          value={form.recipient_address}
                          onChange={(event) =>
                            setForm({ ...form, recipient_address: event.target.value })
                          }
                          className={inputClass}
                        />
                        {parcel.recipient_address_partial && (
                          <span className="mt-1 block text-xs text-slate-400">
                            {de.parcel.onlyCityHint}
                          </span>
                        )}
                      </>
                    ) : (
                      <input
                        value={form[field]}
                        onChange={(event) =>
                          setForm({ ...form, [field]: event.target.value })
                        }
                        className={inputClass}
                      />
                    )}
                  </label>
                ))}
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">
                    {de.parcel.notes}
                  </span>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(event) => setForm({ ...form, notes: event.target.value })}
                    className={inputClass}
                  />
                </label>
                <button
                  onClick={() => updateParcel.mutate({ id: parcel.id, patch: form })}
                  disabled={updateParcel.isPending}
                  className={primaryButton}
                >
                  {de.actions.save}
                </button>
              </section>

              {/* Archiving is the everyday way to clear the board: a parcel is a delivery
                  record, so it gets put away rather than destroyed. Reversible, hence no
                  confirmation. Hiding and deleting below are for administrators. */}
              <section className="space-y-3 border-t border-slate-200 pt-5 dark:border-slate-800">
                <h3 className={sectionHeading}>{de.parcel.sectionActions}</h3>
                <div className="flex flex-wrap gap-2">
                  {/* The way out for a parcel no mail will ever move again. Not reversible
                      the way archiving is, so it asks first. */}
                  {parcel.status !== 'DELIVERED' && (
                    <button
                      onClick={() => {
                        if (window.confirm(de.actions.confirmDelivered)) {
                          markDelivered.mutate(parcel.id)
                        }
                      }}
                      disabled={markDelivered.isPending}
                      title={de.parcel.markDeliveredHint}
                      className={secondaryButton + ' inline-flex items-center gap-1.5'}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                      {de.actions.markDelivered}
                    </button>
                  )}
                  {/* Only something that came in can be sent back; a return of a return is
                      refused by the backend too. */}
                  {!parcel.is_return && (
                    <button
                      onClick={() => setSendingBack(true)}
                      className={secondaryButton + ' inline-flex items-center gap-1.5'}
                    >
                      <Undo2 className="h-3.5 w-3.5" aria-hidden />
                      {de.actions.sendBack}
                    </button>
                  )}
                  <button
                    onClick={() =>
                      updateParcel.mutate({
                        id: parcel.id,
                        patch: { archived: parcel.archived_at == null },
                      })
                    }
                    disabled={updateParcel.isPending}
                    className={secondaryButton + ' inline-flex items-center gap-1.5'}
                  >
                    {parcel.archived_at ? (
                      <>
                        <ArchiveRestore className="h-3.5 w-3.5" aria-hidden />
                        {de.actions.unarchive}
                      </>
                    ) : (
                      <>
                        <Archive className="h-3.5 w-3.5" aria-hidden />
                        {de.actions.archive}
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-400">
                  {parcel.archived_at ? de.parcel.archivedHint : de.parcel.archiveHint}
                </p>
                {/* For a return that came in through a mail and was filed as incoming: the
                    backend re-reads the carrier's return wording when this flips. */}
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={parcel.is_return}
                    disabled={updateParcel.isPending}
                    onChange={(event) =>
                      updateParcel.mutate({
                        id: parcel.id,
                        patch: { is_return: event.target.checked },
                      })
                    }
                    className="mt-0.5 accent-indigo-500"
                  />
                  <span>
                    {de.parcel.markAsReturn}
                    <span className="block text-xs text-slate-400">{de.add.isReturnHint}</span>
                  </span>
                </label>
              </section>

              {isAdmin && (
                <section className="space-y-2 border-t border-slate-200 pt-5 dark:border-slate-800">
                  <h3 className={sectionHeading}>{de.parcel.sectionAdmin}</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() =>
                        setHidden.mutate({ id: parcel.id, hidden: parcel.hidden_at == null })
                      }
                      disabled={setHidden.isPending}
                      className={secondaryButton + ' inline-flex items-center gap-1.5'}
                    >
                      {parcel.hidden_at ? (
                        <>
                          <Eye className="h-3.5 w-3.5" aria-hidden />
                          {de.actions.unhide}
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3.5 w-3.5" aria-hidden />
                          {de.actions.hide}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        if (!window.confirm(de.actions.confirmDeleteParcel)) return
                        deleteParcel.mutate(parcel.id, { onSuccess: onClose })
                      }}
                      disabled={deleteParcel.isPending}
                      className={dangerButton + ' inline-flex items-center gap-1.5'}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      {de.actions.deleteParcel}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    {parcel.hidden_at ? de.parcel.unhideHint : de.parcel.hideHint}
                  </p>
                  <p className="text-xs text-slate-400">{de.parcel.deleteHint}</p>
                </section>
              )}
            </div>
          </>
        )}
      </aside>
      {sendingBack && parcel && (
        <div onClick={(event) => event.stopPropagation()}>
          <AddParcelDialog
            onClose={() => setSendingBack(false)}
            returnOf={{
              id: parcel.id,
              label: `${parcel.description ?? parcel.tracking_number} · ${parcel.carrier_name}`,
              description: parcel.description,
              projectId: parcel.project_id,
            }}
            onCreated={(id) => onOpenParcel?.(id)}
          />
        </div>
      )}
    </div>
  )
}

export function ReturnBadge() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-xs text-sky-800 dark:bg-sky-950 dark:text-sky-200">
      <Undo2 className="h-3 w-3" aria-hidden />
      {de.parcel.isReturn}
    </span>
  )
}

function LinkedParcel({
  label,
  parcel,
  onOpen,
}: {
  label: string
  parcel: ParcelLink
  onOpen?: (id: number) => void
}) {
  const name = `${parcel.description ?? parcel.tracking_number} · ${parcel.carrier_name}`
  return (
    <p className="flex flex-wrap items-center gap-x-2 text-slate-600 dark:text-slate-300">
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}:</span>
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(parcel.id)}
          className="truncate text-indigo-700 underline-offset-2 hover:underline dark:text-indigo-300"
        >
          {name}
        </button>
      ) : (
        <span className="truncate">{name}</span>
      )}
      <StatusRoute status={parcel.status} />
    </p>
  )
}

const sectionHeading = 'text-sm font-semibold text-slate-900 dark:text-slate-100'

function Detail({
  label,
  children,
  wide = false,
}: {
  label: string
  children: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-slate-900 dark:text-slate-100">{children}</dd>
    </div>
  )
}
